import {
	ButtonSpeedDial,
	FloatingLoader,
	NavigationBack,
	Typography,
} from "@/components";
import FloatingSearchV2 from "@/components/ui/floating-search/v2";
import { useDebounce } from "@/hooks/useDebounce";
import { QUERY_KEYS } from "@/libraries/constant";
import { isLink } from "@/libraries/helper";
import {
	getBulletinGroupsOptions,
	getBulletinSubscriptionGroupsOptions,
	getBulletinsOptions,
} from "@/queries";
import { DataIdSchema } from "@/schemas";
import type { Bulletin, BulletinGroup } from "@/types";
import AppBar from "@mui/material/AppBar";
import Drawer from "@mui/material/Drawer";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Toolbar from "@mui/material/Toolbar";
import {
	onlineManager,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import omit from "lodash/omit";
import { useEffect, useMemo, useRef } from "react";
import { z } from "zod";
import BulletinCard from "./-components/BulletinCard";
import DrawerRemoveGroup from "./-components/DrawerRemoveGroup";
import DrawerViewBulletin from "./-components/DrawerViewBulletin";
import EmptyBulletin from "./-components/Empty";

const BulletinSearchSchema = z.object({
	tab: z.number().int().default(0).optional(),
	q: z.string().trim().optional(),
	id: DataIdSchema.optional(),
	gid: DataIdSchema.optional(),
});
type BulletinSearchSchema = z.infer<typeof BulletinSearchSchema>;

export const Route = createFileRoute("/dashboard/bulletin/")({
	validateSearch: zodSearchValidator(BulletinSearchSchema),
	loaderDeps: ({ search }) => ({
		groupId: search.tab,
	}),
	loader: ({ context, deps }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return [
				context.queryClient.ensureQueryData(
					getBulletinSubscriptionGroupsOptions(),
				),
				context.queryClient.ensureQueryData(getBulletinGroupsOptions()),
				context.queryClient.ensureInfiniteQueryData(
					getBulletinsOptions(
						deps.groupId
							? {
									group_id: deps.groupId,
								}
							: undefined,
					),
				),
			];
		}
		return [
			context.queryClient.getQueryData([
				QUERY_KEYS.GET_BULLETIN_SUBSCRIPTION_GROUPS,
			]),
			context.queryClient.getQueryData([QUERY_KEYS.GET_BULLETIN_GROUPS]),
			context.queryClient.getQueryData(
				deps.groupId
					? [QUERY_KEYS.GET_BULLETINS, { group_id: deps.groupId }]
					: [QUERY_KEYS.GET_BULLETINS],
			),
		];
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const activeTab = Route.useSearch({
		select: (state: BulletinSearchSchema) => state.tab ?? 0,
	});
	const searchQ = Route.useSearch({
		select: (state: BulletinSearchSchema) => state.q ?? "",
	});
	const bulletinId = Route.useSearch({
		select: (state: BulletinSearchSchema) => state.id,
	});
	const groupId = Route.useSearch({
		select: (state: BulletinSearchSchema) => state.gid,
	});
	const debouncedSearchQ = useDebounce(searchQ, 500);
	const scrollRef = useRef<HTMLDivElement>(null);

	const subscriptionGroupsQuery = useSuspenseQuery(
		getBulletinSubscriptionGroupsOptions(),
	);
	const bulletinsQuery = useSuspenseInfiniteQuery(
		getBulletinsOptions(activeTab ? { group_id: activeTab } : undefined),
	);
	useSuspenseQuery(getBulletinGroupsOptions());

	const bulletins = useMemo(() => {
		if (!debouncedSearchQ) return bulletinsQuery.data;
		return bulletinsQuery.data.filter(
			(bulletin) =>
				bulletin.title
					?.toLowerCase()
					?.includes(debouncedSearchQ.toLowerCase()) ||
				bulletin.description
					?.toLowerCase()
					?.includes(debouncedSearchQ.toLowerCase()) ||
				bulletin.link
					?.toLowerCase()
					?.includes(debouncedSearchQ.toLowerCase()) ||
				bulletin.image_url
					?.toLowerCase()
					?.includes(debouncedSearchQ.toLowerCase()),
		);
	}, [debouncedSearchQ, bulletinsQuery.data]);

	const bulletin = useMemo(() => {
		if (!bulletinId) return null;
		const selectedBulletin = bulletins.find((b) => b.id === bulletinId);
		if (!selectedBulletin) return null;
		if (selectedBulletin.type === "link") {
			const targetLink = selectedBulletin.link || selectedBulletin.title;
			if (isLink(targetLink)) {
				window.open(targetLink, "_blank");
				return null;
			}
		}
		return selectedBulletin;
	}, [bulletins, bulletinId]);

	const group = useMemo(() => {
		if (!groupId) return null;
		return subscriptionGroupsQuery.data.find((g) => g.id === groupId);
	}, [groupId, subscriptionGroupsQuery.data]);

	const isEmpty = useMemo(
		() =>
			!bulletinsQuery.isLoading &&
			!bulletinsQuery.isFetching &&
			!bulletins.length &&
			!debouncedSearchQ,
		[
			bulletinsQuery.isLoading,
			bulletinsQuery.isFetching,
			bulletins,
			debouncedSearchQ,
		],
	);
	const isEmptySearch = useMemo(
		() =>
			!bulletinsQuery.isLoading &&
			!bulletinsQuery.isFetching &&
			!bulletins.length &&
			!!debouncedSearchQ,
		[
			bulletinsQuery.isLoading,
			bulletinsQuery.isFetching,
			bulletins,
			debouncedSearchQ,
		],
	);

	const currentTab = useMemo(
		() =>
			activeTab > 0
				? subscriptionGroupsQuery.data.find((g) => g.id === activeTab)
				: undefined,
		[activeTab, subscriptionGroupsQuery.data],
	);

	const bulletinsVirtualizer = useVirtualizer({
		count: bulletins.length,
		gap: 12,
		overscan: 5,
		paddingStart: 16,
		paddingEnd: bulletins.length ? 229 : undefined,
		estimateSize: () => 128,
		getScrollElement: () => scrollRef.current,
		measureElement: (el) => el.getBoundingClientRect().height,
	});
	const bulletinsVirtualizerItems = bulletinsVirtualizer.getVirtualItems();

	const handleChangeTab = (e: React.SyntheticEvent, newValue: number) => {
		e.preventDefault();
		return navigate({
			// @ts-expect-error - tab is optional
			search: (oldSearch) => ({
				...omit(oldSearch, ["tab"]),
				tab: newValue || undefined,
			}),
			replace: true,
		});
	};

	const onChangeSearch = (value: string) => {
		return navigate({
			// @ts-expect-error - q is optional
			search: (oldSearch) => ({
				...oldSearch,
				q: value || undefined,
			}),
			replace: true,
		});
	};

	const onClickBulletin = (
		e: React.MouseEvent<HTMLDivElement>,
		bulletin: Bulletin,
	) => {
		e.preventDefault();
		return navigate({
			// @ts-expect-error - id is optional
			search: (oldSearch) => ({
				...oldSearch,
				id: bulletin.id,
			}),
		});
	};

	const onContextMenuGroup = (
		e: React.MouseEvent<HTMLDivElement>,
		group: BulletinGroup,
	) => {
		e.preventDefault();
		return navigate({
			// @ts-expect-error - gid is optional
			search: (oldSearch) => ({
				...oldSearch,
				gid: group.id,
			}),
		});
	};

	const onCloseBulletin = () => {
		return navigate({
			// @ts-expect-error - id is optional
			search: (oldSearch) => ({
				...omit(oldSearch, ["id"]),
				id: undefined,
			}),
		});
	};

	const onCloseGroup = () => {
		return navigate({
			// @ts-expect-error - gid is optional
			search: (oldSearch) => ({
				...omit(oldSearch, ["gid"]),
				gid: undefined,
			}),
		});
	};

	const onClickSetting = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		return navigate({
			to: "/dashboard/bulletin/setting",
		});
	};

	useEffect(() => {
		const [lastItem] = [...bulletinsVirtualizerItems].reverse();

		if (!lastItem) return;

		if (
			lastItem.index >= bulletins.length - 1 &&
			bulletinsQuery.hasNextPage &&
			!bulletinsQuery.isFetchingNextPage
		) {
			bulletinsQuery.fetchNextPage();
		}
	}, [
		bulletinsQuery.hasNextPage,
		bulletinsQuery.fetchNextPage,
		bulletinsQuery.isFetchingNextPage,
		bulletins.length,
		bulletinsVirtualizerItems,
	]);

	return (
		<>
			<AppBar
				position="relative"
				color="transparent"
				className="[&_*_]:text-white !bg-gradient-bulletin"
			>
				<div className="container">
					<Toolbar classes={{ root: "!p-0" }}>
						<NavigationBack />
						<div className="w-full relative flex gap-1 justify-between py-3">
							<FloatingSearchV2
								name="search"
								value={searchQ}
								onClear={() => onChangeSearch("")}
								onChange={(e) => onChangeSearch(e.target.value)}
								className="!rounded-xl"
								inputClassName="!rounded-xl"
							>
								<div className="flex items-center">
									<Typography fontWeight={700} fontSize={16}>
										Bulletin
									</Typography>
								</div>
							</FloatingSearchV2>
						</div>
					</Toolbar>
					{subscriptionGroupsQuery.data.length ? (
						<Toolbar classes={{ root: "!p-0 !items-end" }}>
							<Tabs
								value={currentTab?.id ?? 0}
								onChange={handleChangeTab}
								indicatorColor="primary"
								textColor="inherit"
								variant="scrollable"
								aria-label="full width tabs example"
								className="w-full [&_span.MuiTabs-indicator]:hidden"
							>
								<Tab
									label="All"
									value={0}
									sx={{ textTransform: "none" }}
									className="!bg-gray-light !text-zinc-900 !rounded-t-xl"
								/>
								{subscriptionGroupsQuery.data.map((group) => (
									<Tab
										key={group.id}
										label={group.name}
										value={group.id}
										sx={{ textTransform: "none", backgroundColor: group.color }}
										className="!rounded-t-xl"
										onContextMenu={(e) => onContextMenuGroup(e, group)}
									/>
								))}
							</Tabs>
						</Toolbar>
					) : null}
				</div>
			</AppBar>
			<div
				ref={scrollRef}
				className="relative h-full w-full py-4 bg-gray-light overflow-y-auto"
				style={{
					backgroundColor: currentTab?.color,
				}}
			>
				<FloatingLoader
					open={
						bulletinsQuery.isLoading ||
						bulletinsQuery.isFetching ||
						bulletinsQuery.isFetchingNextPage
					}
				/>
				{!isEmpty || !isEmptySearch ? (
					<div
						className="relative flex flex-col gap-3"
						style={{
							height: bulletinsVirtualizer.getTotalSize(),
						}}
					>
						{bulletinsVirtualizerItems.map((bulletinsVirtualizerItem) => {
							const bulletin = bulletins[bulletinsVirtualizerItem.index];
							return (
								// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
								<div
									key={bulletinsVirtualizerItem.key}
									ref={bulletinsVirtualizer.measureElement}
									className="container absolute top-0 left-0"
									style={{
										transform: `translateY(${bulletinsVirtualizerItem.start}px)`,
									}}
									onClick={(e) => onClickBulletin(e, bulletin)}
								>
									<BulletinCard bulletin={bulletin} />
								</div>
							);
						})}
					</div>
				) : null}
				{isEmpty ? <EmptyBulletin /> : null}
				{isEmptySearch ? <EmptyBulletin title="No Bulletin" /> : null}
			</div>
			<Drawer anchor="right" open={!!bulletin} onClose={onCloseBulletin}>
				<DrawerViewBulletin
					bulletin={bulletin}
					onClickClose={onCloseBulletin}
				/>
			</Drawer>
			<Drawer
				anchor="bottom"
				open={!!group}
				onClose={onCloseGroup}
				classes={{
					paper: "!rounded-t-xl",
				}}
			>
				<DrawerRemoveGroup
					group={group}
					groupSubscriptions={subscriptionGroupsQuery.data}
					onClickClose={onCloseGroup}
				/>
			</Drawer>
			<ButtonSpeedDial mb={0} bottom={0} onClickSetting={onClickSetting} />
		</>
	);
}
